import {
  doc,
  getDoc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type TestSuite } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter } from './firestoreUtils.js';
import type { TestSuiteRepository } from '@/infrastructure/repositories/RepositoryInterfaces.js';

const TEST_SUITES = 'testSuites';

const converter = buildConverter<TestSuite>(
  (data) => data as TestSuite,
  (model) => ({ ...model, createdAt: serverTimestamp(), updatedAt: serverTimestamp() })
);

export class FirestoreTestSuiteRepository implements TestSuiteRepository {
  async findAll(): Promise<TestSuite[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(collection(db, TEST_SUITES), where('environment', '==', getCurrentEnvironment())).withConverter(
      converter
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findById(id: string): Promise<TestSuite | undefined> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const snap = await getDoc(doc(db, TEST_SUITES, id).withConverter(converter));
    return snap.data();
  }

  async findByProjectId(projectId: string): Promise<TestSuite[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, TEST_SUITES),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment())
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(suite: TestSuite): Promise<TestSuite> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const existing = await this.findById(suite.id);
    const data = { ...suite, environment: getCurrentEnvironment() };
    if (existing) {
      await updateDoc(doc(db, TEST_SUITES, suite.id), { ...data, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, TEST_SUITES, suite.id).withConverter(converter), data);
    }
    return { ...data, createdAt: existing?.createdAt ?? new Date().toISOString(), updatedAt: new Date().toISOString() };
  }

  async delete(id: string): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await deleteDoc(doc(db, TEST_SUITES, id));
  }

  async duplicate(id: string, newName?: string): Promise<TestSuite | undefined> {
    const original = await this.findById(id);
    if (!original) return undefined;
    const duplicated: TestSuite = {
      ...original,
      id: `suite-${Date.now()}`,
      name: newName || `${original.name} (Copy)`,
      version: this.bumpVersion(original.version),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    await this.save(duplicated);
    return duplicated;
  }

  private bumpVersion(version: string): string {
    const parts = version.split('.');
    const last = parts.pop() ?? '0';
    const next = Number.isNaN(Number(last)) ? 1 : Number(last) + 1;
    return [...parts, String(next)].join('.');
  }
}

export const createFirestoreTestSuiteRepository = (): FirestoreTestSuiteRepository =>
  new FirestoreTestSuiteRepository();
